/* eslint-disable react-hooks/exhaustive-deps */
import PropTypes from 'prop-types';
import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Avatar,
  IconButton,
  ClickAwayListener,
  Popper,
  Grow,
  Paper,
  Skeleton,
  Dialog,
  DialogContent,
  CircularProgress,
} from '@mui/material';
import { Iconify } from 'src/components';
import TreatmentFlowComponent from 'src/components/treatmentComponents/TreatmentFlowComponent';
import TreatmentEditPopover from 'src/pages/dashboard/tables/AllLeadTable/TreatmentEditPopover';
import { dispatch, useSelector } from 'src/redux/store';
import { updateSubmission, getFormSubmission, getSubmissionSection } from 'src/redux/slices/callScript';
import { getPracticeUserListing, clearPracticeUserData } from 'src/redux/slices/practiceUser';
import { AssignTaskPopup } from 'src/components/dialogPopUp';
import useSnackbar from 'src/hooks/useCustomSnackbar';
import { callApi } from 'src/utils/apiCall';
import { CANONICAL_SALUTATION_VALUES } from 'src/utils/getReferralData';

// Import components from callScriptV2 (read-only imports, no modifications)
import {
  CallScriptHeader,
  SectionTabs,
  QuestionProgress,
  ScriptCard,
  StickyFooter,
  ContextPanel,
  SayBlock,
  TipBlock,
  SectionDivider,
  NoteToggle,
  FormRow,
  ScriptTextField,
  ScriptSelect,
  ScriptTextarea,
  ScriptDateField,
  ChoiceGrid,
  ResponseButtons,
  InterestCards,
  ScaleRating,
  LeadSearchField,
  HintAlert,
  CaptureContactDetailsLive,
  ConfirmSpeakerLive,
} from 'src/components/callScriptV2/components';

import {
  appContainerSx,
  mainContainerSx,
  scriptPanelSx,
  cardAreaSx,
} from 'src/components/callScriptV2/styles/styles';

import { matchSubmissionToScript } from './utils';

/**
 * Helper to safely join name parts
 */
const safeJoinName = (firstName, lastName) => {
  const parts = [firstName, lastName].filter(Boolean);
  return parts.join(' ') || '';
};

/**
 * Helper to get treatment name from treatment object
 */
const getTreatmentName = (treatment) => {
  if (!treatment) return '';
  if (typeof treatment === 'string') return treatment;
  return treatment.name || treatment.treatment_name || '';
};

/**
 * Helper to extract $Hint: patterns from text
 */
const extractHint = (text) => {
  if (!text || typeof text !== 'string') return { text, hint: null };
  const hintRegex = /\$Hint:\s*(.+?)(?=\$[A-Za-z]|$)/gi;
  const matches = text.match(hintRegex);
  if (!matches || matches.length === 0) {
    return { text, hint: null };
  }
  let hint = matches[0].replace(/\$Hint:\s*/i, '').trim();
  hint = hint.replace(/\\s/g, ' ').replace(/\\/g, '');
  const cleanText = text.replace(hintRegex, '').trim();
  return { text: cleanText, hint };
};

/**
 * SavedCallScriptViewer - View/Edit saved call script submissions
 *
 * This component provides a full-screen dialog to view and edit saved form submissions
 * using the same UI as CallScriptV2.
 */
const SavedCallScriptViewer = ({
  open,
  onClose,
  formId,
  submissionId,
  leadDetail,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const { practiceId } = useSelector((state) => state.practiceTreatmentList);
  const { submissionData } = useSelector((state) => state.callScript);
  const mergeTags = useSelector((state) => state.mergeTags);
  const { salutationOptions } = useSelector((state) => state.schema);
  const { practiceUser } = useSelector((state) => state.practiceUser);

  // Loading states
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  // Script data state
  const [scriptData, setScriptData] = useState(null);

  // State
  const [currentCardIndex, setCurrentCardIndex] = useState(0);
  const [formData, setFormData] = useState({});
  const [completedCards, setCompletedCards] = useState(new Set());
  const [skippedCards, setSkippedCards] = useState(new Set());
  const [cardNotes, setCardNotes] = useState({});
  const [saveStatus, setSaveStatus] = useState('idle');
  const [contextPanelOpen, setContextPanelOpen] = useState(false);
  const [callNotes] = useState('');
  const [validationErrors, setValidationErrors] = useState([]);

  // Special field state
  const [assignToState, setAssignToState] = useState([]);
  const [treatmentPopoverOpen, setTreatmentPopoverOpen] = useState(false);
  const [treatmentAnchorEl, setTreatmentAnchorEl] = useState(null);
  const treatmentButtonRef = useRef(null);
  const [, setUserAutoSelected] = useState(false);
  const [assigneePopupOpen, setAssigneePopupOpen] = useState(false);
  const [assigneeAnchorEl, setAssigneeAnchorEl] = useState(null);

  // Track field submission map for updates
  const fieldSubmissionMapRef = useRef({});
  // Track special attributes (address, leadType, etc.) with their field and submission IDs
  const specialAttributeMapRef = useRef({});

  // Original script state for section matching
  const [originalScript, setOriginalScript] = useState(null);
  const [scriptLoadError, setScriptLoadError] = useState(false);
  const [scriptLoading, setScriptLoading] = useState(true); // Start as true to wait for script load

  // Fetch submission data on mount
  useEffect(() => {
    if (open && formId && submissionId && practiceId) {
      setIsLoading(true);
      dispatch(
        getFormSubmission({
          practiceId,
          formId,
          submissionId,
        })
      );
    }
    return () => {
      dispatch(getSubmissionSection([]));
    };
  }, [open, formId, submissionId, practiceId]);

  // Fetch original script when submission data loads
  useEffect(() => {
    const fetchOriginalScript = async () => {
      if (!submissionData?.form?.id || !practiceId) return;

      const scriptId = submissionData.form.id;
      setScriptLoading(true);
      setScriptLoadError(false);

      try {
        // Fetch the form with specific fields needed for matching sections
        const fieldsParam = '{id,name,sections{id,name,description,order,fields{id,label,description,order,attribute,is_required,field_type,choices,metadata}}}';
        const response = await callApi(`/api/v1/practices/${practiceId}/forms/${scriptId}/?fields=${fieldsParam}`);
        // callApi returns { ok, status, data, headers } - actual form data is in response.data
        const formData = response?.data;
        // eslint-disable-next-line no-console
        console.log('[fetchOriginalScript] API Response:', {
          ok: response?.ok,
          status: response?.status,
          hasData: !!formData,
          formId: formData?.id,
          sectionsCount: formData?.sections?.length,
          sectionDetails: formData?.sections?.map((s) => ({
            id: s.id,
            name: s.name,
            fieldsCount: s.fields?.length,
            firstFieldId: s.fields?.[0]?.id,
          })),
        });
        if (response?.ok && formData?.id) {
          setOriginalScript(formData);
        } else {
          setScriptLoadError(true);
        }
      } catch (error) {
        // Script deleted or not accessible - show all as "Other Responses"
        console.warn('Original script not found:', error);
        setScriptLoadError(true);
      } finally {
        setScriptLoading(false);
      }
    };

    fetchOriginalScript();
  }, [submissionData?.form?.id, practiceId]);

  // Helper to build cards from matched sections
  const buildSectionCards = useCallback((matchedSections) => {
    const cards = [];
    const sortedSections = Object.values(matchedSections).sort(
      (a, b) => (a.order || 0) - (b.order || 0)
    );

    sortedSections.forEach((section, sectionIdx) => {
      const badgeClass = `s${((sectionIdx + 1) % 7) + 1}`;

      section.fields.forEach((submittedField, fieldIdx) => {
        const field = submittedField.field || submittedField.originalField;
        if (!field) return;

        // Get the original field from the script (has the actual choices/options)
        const {originalField} = submittedField;

        // Build card from original field definition
        const card = {
          section: sectionIdx + 2, // +2 because section 1 is Basic Info
          question: fieldIdx + 1,
          badge: `Section ${sectionIdx + 2}.${fieldIdx + 1} · ${section.name}`,
          badgeClass,
          title: field.label || field.field_name || 'Field',
          subtitle: '',
          fieldId: field.id,
          submissionEntryId: submittedField.id,
          elements: [],
        };

        // Add description as "Say" block if present
        if (field.description) {
          card.elements.push({
            type: 'say',
            text: field.description,
            tag: 'Say',
          });
        }

        // Transform field to element based on type
        const fieldType = field.field_type?.field_type || field.type;
        // IMPORTANT: Check field.attribute first for special attributes, then fall back to field_type.attribute
        const specialAttribute = field.attribute;
        const genericAttribute = field.field_type?.attribute;
        const uiType = field.field_type?.ui_type || originalField?.field_type?.ui_type;
        // IMPORTANT: Get choices from originalField first (from script definition), fall back to submission field
        // Submission field may have empty choices array, but original script has the actual options
        let choices = originalField?.field_type?.choices || originalField?.choices || field.field_type?.choices || field.choices || [];

        // For lead_source_type internal fields, choices are not stored on the field definition
        // They come from schema/constants - provide fallback options when empty
        if ((genericAttribute === 'lead_source_type' || specialAttribute === 'lead_source_type') && choices.length === 0) {
          choices = ['PHONE', 'EMAIL', 'WALK_IN', 'WEBSITE', 'FACEBOOK', 'GOOGLE', 'INSTAGRAM', 'REFERRAL', 'EVENTS', 'OTHER'];
        }

        // Build element based on field type
        let element = null;

        // Get the submitted value for this field
        const submittedValue = submittedField.value;

        // Check for special attributes FIRST (these are custom call script field types)
        if (specialAttribute === 'capture_contact_details') {
          element = { type: 'capture_contact_details', field: 'contactDetails', id: field.id, label: field.label, value: submittedValue };
        } else if (specialAttribute === 'confirm_speaker') {
          element = { type: 'confirm_speaker', field: 'confirmSpeaker', id: field.id, label: field.label, value: submittedValue };
        } else if (specialAttribute === 'answer_the_call') {
          element = { type: 'answer_the_call', field: 'answerTheCallCompletedBy', id: field.id, label: field.label, value: submittedValue };
        } else if (specialAttribute === 'identify_caller') {
          element = { type: 'identify_caller', field: 'identifyCaller', id: field.id, label: field.label, value: submittedValue };
        } else if (specialAttribute === 'reason_for_calling') {
          element = { type: 'reason_for_calling', field: 'reasonForCall', id: field.id, label: field.label, value: submittedValue };
        } else if (specialAttribute === 'close_call') {
          element = { type: 'close_call', field: 'closeCall', id: field.id, label: field.label, value: submittedValue };
        } else if (fieldType === 'lead_search_select' || genericAttribute === 'lead_search') {
          element = { type: 'lead_search_select', field: 'leadName', id: field.id, label: field.label, required: field.is_required };
        } else if (fieldType === 'practice_treatment_search_select' || genericAttribute === 'treatment_search') {
          element = { type: 'practice_treatment_search_select', field: 'treatment', id: field.id, label: field.label, required: field.is_required };
        } else if (fieldType === 'practice_user_search_select' || genericAttribute === 'user_search') {
          element = { type: 'practice_user_search_select', field: 'bookedWith', id: field.id, label: field.label, required: field.is_required };
        } else if (fieldType === 'char') {
          element = { type: 'textField', field: `field_${field.id}`, id: field.id, label: field.label, placeholder: field.placeholder, required: field.is_required };
        } else if (fieldType === 'choice') {
          if (uiType === 'DROPDOWN') {
            element = { type: 'select', field: `field_${field.id}`, id: field.id, label: field.label, options: choices, required: field.is_required };
          } else if (uiType === 'MULTI_SELECT' || uiType === 'CHECKBOX') {
            element = { type: 'choiceGrid', field: `field_${field.id}`, id: field.id, options: choices, choiceType: 'checkbox' };
          } else if (uiType === 'RADIO') {
            element = { type: 'choiceGrid', field: `field_${field.id}`, id: field.id, options: choices, choiceType: 'radio', single: true };
          } else {
            element = { type: 'select', field: `field_${field.id}`, id: field.id, label: field.label, options: choices, required: field.is_required };
          }
        } else if (fieldType === 'boolean') {
          element = { type: 'choiceGrid', field: `field_${field.id}`, id: field.id, options: ['Yes', 'No'], choiceType: 'radio', single: true };
        } else if (fieldType === 'datetime' || fieldType === 'date') {
          element = { type: 'date', field: `field_${field.id}`, id: field.id, label: field.label, required: field.is_required };
        } else if (fieldType === 'text' || fieldType === 'textarea' || fieldType === 'long_text') {
          element = { type: 'textarea', field: `field_${field.id}`, id: field.id, label: field.label, placeholder: field.placeholder, required: field.is_required, rows: 3 };
        } else if (fieldType === 'label') {
          // Label type - already added description as say block
          element = null;
        } else {
          // Default to text field
          element = { type: 'textField', field: `field_${field.id}`, id: field.id, label: field.label, placeholder: field.placeholder, required: field.is_required };
        }

        if (element) {
          card.elements.push(element);
        }

        if (card.elements.length > 0) {
          cards.push(card);
        }
      });
    });

    return cards;
  }, []);

  // Helper to build unmatched section cards
  const buildUnmatchedCards = useCallback((unmatchedFields, startSectionId) => {
    if (!unmatchedFields || unmatchedFields.length === 0) return [];

    const cards = [];
    unmatchedFields.forEach((submittedField, idx) => {
      const { field } = submittedField;
      if (!field) return;

      const card = {
        section: startSectionId,
        question: idx + 1,
        badge: `Other · Response ${idx + 1}`,
        badgeClass: 's7', // Gray/neutral color
        title: field.label || field.field_name || `Response ${idx + 1}`,
        subtitle: 'This field is not in the current script version',
        fieldId: field.id,
        submissionEntryId: submittedField.id,
        isUnmatched: true,
        elements: [],
      };

      // Add description if present
      if (field.description) {
        card.elements.push({
          type: 'say',
          text: field.description,
          tag: 'Say',
        });
      }

      // Transform field to element
      const fieldType = field.field_type?.field_type || field.type;
      const uiType = field.field_type?.ui_type;
      const choices = field.field_type?.choices || field.choices || [];

      let element = null;
      if (fieldType === 'choice') {
        if (uiType === 'DROPDOWN') {
          element = { type: 'select', field: `field_${field.id}`, id: field.id, label: field.label, options: choices, required: field.is_required };
        } else if (uiType === 'MULTI_SELECT' || uiType === 'CHECKBOX') {
          element = { type: 'choiceGrid', field: `field_${field.id}`, id: field.id, options: choices, choiceType: 'checkbox' };
        } else if (uiType === 'RADIO') {
          element = { type: 'choiceGrid', field: `field_${field.id}`, id: field.id, options: choices, choiceType: 'radio', single: true };
        } else {
          element = { type: 'select', field: `field_${field.id}`, id: field.id, label: field.label, options: choices };
        }
      } else if (fieldType === 'boolean') {
        element = { type: 'choiceGrid', field: `field_${field.id}`, id: field.id, options: ['Yes', 'No'], choiceType: 'radio', single: true };
      } else if (fieldType === 'datetime' || fieldType === 'date') {
        element = { type: 'date', field: `field_${field.id}`, id: field.id, label: field.label };
      } else if (fieldType === 'text' || fieldType === 'textarea' || fieldType === 'long_text') {
        element = { type: 'textarea', field: `field_${field.id}`, id: field.id, label: field.label, rows: 3 };
      } else {
        element = { type: 'textField', field: `field_${field.id}`, id: field.id, label: field.label, placeholder: field.placeholder };
      }

      if (element) {
        card.elements.push(element);
      }

      if (card.elements.length > 0) {
        cards.push(card);
      }
    });

    return cards;
  }, []);

  // Process submission data when loaded (with section matching)
  useEffect(() => {
    // Wait for submission data and script loading to complete
    if (!submissionData?.fields?.length || scriptLoading) return;

    const formMeta = submissionData.form || {};
    const submissionDate = submissionData.created_at_human || '';
    const formCategory = formMeta.category || 'INBOUND';

    // Section 1: Basic Information card (non-editable lead details)
    const basicElements = [
      { type: 'textField', field: 'basic_name', label: 'Patient Name', disabled: true },
      { type: 'textField', field: 'basic_enquiry', label: 'Type of Enquiry', disabled: true },
      { type: 'textField', field: 'basic_treatment', label: 'Treatment', disabled: true },
    ];
    if (leadDetail?.lead_contact?.phone) {
      basicElements.push({ type: 'textField', field: 'basic_phone', label: 'Phone', disabled: true });
    }
    if (leadDetail?.lead_contact?.email) {
      basicElements.push({ type: 'textField', field: 'basic_email', label: 'Email', disabled: true });
    }

    const basicInfoCard = {
      section: 1,
      question: 1,
      badge: 'Section 1.1 · Basic Information',
      badgeClass: 's1',
      title: 'Basic Information',
      subtitle: 'Lead details for this submission',
      elements: basicElements,
    };

    let allCards = [basicInfoCard];
    const allSections = [{ id: 1, name: 'Basic Information' }];

    // Filter enabled fields from submission
    const enabledSubmissionFields = submissionData.fields.filter(
      (sf) => sf?.field && sf.field.is_enabled !== false
    );

    if (originalScript && !scriptLoadError && originalScript.sections) {
      // Match fields to original script sections
      const { matched, unmatched } = matchSubmissionToScript(
        enabledSubmissionFields,
        originalScript.sections
      );

      // Build cards from matched sections
      const matchedCards = buildSectionCards(matched);
      allCards = [...allCards, ...matchedCards];

      // Build sections array from matched sections
      const sortedMatchedSections = Object.values(matched).sort(
        (a, b) => (a.order || 0) - (b.order || 0)
      );
      sortedMatchedSections.forEach((section, idx) => {
        allSections.push({
          id: idx + 2,
          name: section.name,
          originalId: section.id,
        });
      });

      // Add unmatched section if there are unmatched fields
      if (unmatched.length > 0) {
        const unmatchedSectionId = allSections.length + 1;
        const unmatchedCards = buildUnmatchedCards(unmatched, unmatchedSectionId);
        allCards = [...allCards, ...unmatchedCards];
        allSections.push({
          id: unmatchedSectionId,
          name: 'Other Responses',
          isUnmatched: true,
        });
      }
    } else {
      // Script deleted or error - show all fields in "Other Responses"
      const unmatchedCards = buildUnmatchedCards(enabledSubmissionFields, 2);
      allCards = [...allCards, ...unmatchedCards];
      allSections.push({
        id: 2,
        name: 'Other Responses',
        isUnmatched: true,
      });
    }

    setScriptData({
      title: 'Submitted Callscript',
      subtitle: `${formMeta.name || 'Call Script'}${submissionDate ? ` · ${submissionDate}` : ''}`,
      sections: allSections,
      cards: allCards,
      scriptLoadError,
    });

    // Build field submission map for updates, including special attributes
    const fieldMap = {};
    const specialAttributeMap = {}; // Maps attribute name to field info for special attributes
    submissionData.fields?.forEach((field) => {
      const fieldId = field?.field?.id;
      const submissionFieldId = field?.id;

      if (fieldId !== undefined && submissionFieldId !== undefined) {
        fieldMap[String(fieldId)] = submissionFieldId;
      }

      // Also track special attributes so we can save them back with their original field/entry IDs
      const attribute = field?.field?.field_type?.attribute || field?.field?.attribute;
      if (attribute && submissionFieldId !== undefined) {
        specialAttributeMap[attribute] = {
          fieldId,
          submissionFieldId,
        };
      }
    });
    fieldSubmissionMapRef.current = fieldMap;
    specialAttributeMapRef.current = specialAttributeMap;

    // Initialize form data with saved values
    const initialFormData = {
      lead_id: submissionData.lead || leadDetail?.id || '',
      firstName: leadDetail?.first_name || '',
      lastName: leadDetail?.last_name || '',
      title: leadDetail?.salutation || '',
      phone: leadDetail?.lead_contact?.phone || '',
      phone_number: leadDetail?.lead_contact?.phone || '',
      email: leadDetail?.lead_contact?.email || '',
      // Date of birth from lead
      date_of_birth: leadDetail?.date_of_birth || leadDetail?.dob || '',
      // Address fields from lead contact
      address_line_1: leadDetail?.lead_contact?.address_line_1 || leadDetail?.lead_contact?.address?.line1 || '',
      address_line_2: leadDetail?.lead_contact?.address_line_2 || leadDetail?.lead_contact?.address?.line2 || '',
      city: leadDetail?.lead_contact?.city || leadDetail?.lead_contact?.address?.city || '',
      county: leadDetail?.lead_contact?.county || leadDetail?.lead_contact?.address?.county || '',
      postcode: leadDetail?.lead_contact?.postcode || leadDetail?.lead_contact?.address?.postcode || '',
      treatment: getTreatmentName(leadDetail?.lead_treatments?.[0]?.treatment),
      treatmentValue: leadDetail?.lead_treatments?.[0]?.price || '',
      treatment_id: leadDetail?.lead_treatments?.[0]?.id || '',
      // Basic info section values (non-editable)
      basic_name: `${leadDetail?.salutation || ''} ${leadDetail?.first_name || ''} ${leadDetail?.last_name || ''}`.trim() || 'Unknown',
      basic_enquiry: formCategory,
      basic_treatment: getTreatmentName(leadDetail?.lead_treatments?.[0]?.treatment) || 'Not specified',
      basic_phone: leadDetail?.lead_contact?.phone || '',
      basic_email: leadDetail?.lead_contact?.email || '',
    };

    // Map saved values from submission to form fields
    submissionData.fields?.forEach((submissionField) => {
      const { field, value } = submissionField;
      if (!field) return;

      const fieldType = field.field_type?.field_type;
      const attribute = field.field_type?.attribute || field.attribute;

      // Always store by field ID for element lookup
      if (field.id) {
        if (fieldType === 'choice') {
          const uiType = field.field_type?.ui_type;
          if (uiType === 'MULTI_SELECT' || uiType === 'CHECKBOX') {
            initialFormData[`field_${field.id}`] = value ? value.split(' | ') : [];
          } else {
            initialFormData[`field_${field.id}`] = value;
          }
        } else {
          initialFormData[`field_${field.id}`] = value;
        }
      }

      // Also store by named keys for special attributes
      // Use leadDetail as fallback when submission value is empty
      if (attribute === 'phone') {
        const phoneValue = value || leadDetail?.lead_contact?.phone || '';
        initialFormData.phone_number = phoneValue;
        initialFormData.phone = phoneValue;
        if (field.id) initialFormData[`field_${field.id}`] = phoneValue;
      } else if (attribute === 'lead_email') {
        const emailValue = value || leadDetail?.lead_contact?.email || '';
        initialFormData.email = emailValue;
        if (field.id) initialFormData[`field_${field.id}`] = emailValue;
      } else if (attribute === 'lead_dob') {
        // Date of birth field - fallback to leadDetail
        const dobValue = value || leadDetail?.date_of_birth || leadDetail?.dob || '';
        initialFormData.date_of_birth = dobValue;
        if (field.id) initialFormData[`field_${field.id}`] = dobValue;
      } else if (attribute === 'lead_address') {
        initialFormData.address = value;
      } else if (attribute === 'lead_source_type') {
        initialFormData.leadType = value;
      } else if (attribute === 'lead_is_registered_patient') {
        initialFormData.isRegister = value;
      } else if (attribute === 'lead_preferred_contact_method') {
        initialFormData.preferredContactMethod = value;
      } else if (attribute === 'user_search' || fieldType === 'practice_user_search_select') {
        initialFormData.bookedWithIds = value ? value.split(' | ').map((id) => id.trim()) : [];
      }
    });

    setFormData(initialFormData);
    setIsLoading(false);
  }, [submissionData, leadDetail, originalScript, scriptLoadError, scriptLoading, buildSectionCards, buildUnmatchedCards]);

  // Fetch practice users for assignee selector
  useEffect(() => {
    if (practiceId && open) {
      dispatch(getPracticeUserListing({ practiceId, role: 'TCO,USER' }));
    }
  }, [practiceId, open]);

  // Hydrate user IDs to full user objects when practice users are loaded
  useEffect(() => {
    if (practiceUser?.results && formData.bookedWithIds?.length > 0 && assignToState.length === 0) {
      const hydratedUsers = formData.bookedWithIds
        .map((userId) => {
          // Find user by ID in practice users list
          const user = practiceUser.results.find((pu) => String(pu.id) === String(userId));
          return user || null;
        })
        .filter(Boolean);

      if (hydratedUsers.length > 0) {
        setAssignToState(hydratedUsers);
        setFormData((prev) => ({ ...prev, bookedWith: hydratedUsers }));
      }
    }
  }, [practiceUser?.results, formData.bookedWithIds, assignToState.length]);

  // Build merge tag data
  const mergeTagData = useMemo(() => {
    const data = {};
    if (leadDetail) {
      data.lead_first_name = leadDetail.first_name || '';
      data.lead_last_name = leadDetail.last_name || '';
      data.lead_full_name = safeJoinName(leadDetail.first_name, leadDetail.last_name);
      data.lead_email = leadDetail.lead_contact?.email || '';
      data.lead_phone = leadDetail.lead_contact?.phone || '';
      data.lead_salutation = leadDetail.salutation || '';

      const primaryTreatment = leadDetail.lead_treatments?.[0];
      if (primaryTreatment) {
        data.treatment_name = getTreatmentName(primaryTreatment.treatment);
        data.treatment_price = primaryTreatment.price || '';
      }
    }
    if (mergeTags?.mtValLeads) Object.assign(data, mergeTags.mtValLeads);
    if (mergeTags?.mtValTreatments) Object.assign(data, mergeTags.mtValTreatments);
    if (mergeTags?.mtValPractice) Object.assign(data, mergeTags.mtValPractice);
    if (mergeTags?.mtValUser) Object.assign(data, mergeTags.mtValUser);
    return data;
  }, [leadDetail, mergeTags]);

  // Get cards from script data
  const cards = useMemo(() => scriptData?.cards || [], [scriptData]);
  const baseSections = useMemo(() => scriptData?.sections || [], [scriptData]);

  // Sections (no closure section for saved viewer - just submit)
  const sections = useMemo(() => baseSections, [baseSections]);

  // Current card data
  const currentCard = cards[currentCardIndex];
  const currentSectionId = currentCard?.section || 1;

  // Calculate progress
  const totalSteps = cards.length;
  const currentStep = Math.min(currentCardIndex + 1, totalSteps);

  // Section progress calculations
  const completedSections = useMemo(() => {
    const completed = [];
    sections.forEach((section) => {
      const sectionCards = cards.filter((c) => c.section === section.id);
      const sectionIndices = sectionCards.map((c) => cards.indexOf(c));
      const allDone = sectionIndices.every(
        (idx) => completedCards.has(idx) || skippedCards.has(idx)
      );
      const lastCardIndex = Math.max(...sectionIndices);
      if (allDone && currentCardIndex > lastCardIndex) {
        completed.push(section.id);
      }
    });
    return completed;
  }, [sections, cards, completedCards, skippedCards, currentCardIndex]);

  const sectionsWithSkipped = useMemo(() => {
    const withSkipped = [];
    sections.forEach((section) => {
      const sectionCards = cards.filter((c) => c.section === section.id);
      const sectionIndices = sectionCards.map((c) => cards.indexOf(c));
      if (sectionIndices.some((idx) => skippedCards.has(idx))) {
        withSkipped.push(section.id);
      }
    });
    return withSkipped;
  }, [sections, cards, skippedCards]);

  // Questions for current section
  const currentSectionQuestions = useMemo(
    () => cards.filter((c) => c.section === currentSectionId),
    [cards, currentSectionId]
  );

  const currentQuestionInSection = useMemo(
    () =>
      currentSectionQuestions.findIndex((c) => cards.indexOf(c) === currentCardIndex),
    [currentSectionQuestions, cards, currentCardIndex]
  );

  // Progress data for context panel
  const progressData = useMemo(
    () => ({
      current: currentStep,
      total: totalSteps,
      sections: sections.map((s) => {
        const sectionCards = cards.filter((c) => c.section === s.id);
        const sectionIndices = sectionCards.map((c) => cards.indexOf(c));
        const isActive = currentSectionId === s.id;
        const isCompleted = completedSections.includes(s.id);
        const isSkipped = sectionIndices.some((idx) => skippedCards.has(idx));
        return {
          id: s.id,
          name: s.name,
          isActive,
          isCompleted,
          isSkipped,
        };
      }),
    }),
    [currentStep, totalSteps, sections, cards, currentSectionId, completedSections, skippedCards]
  );

  // Collected data for context panel
  const collectedData = useMemo(() => {
    const data = [];
    if (formData.firstName) {
      data.push({
        label: 'Name',
        value: `${formData.title || ''} ${formData.firstName} ${formData.lastName || ''}`.trim(),
      });
    }
    if (formData.phone || formData.phone_number) {
      data.push({ label: 'Phone', value: formData.phone || formData.phone_number });
    }
    if (formData.email) {
      data.push({ label: 'Email', value: formData.email });
    }
    if (formData.treatment) {
      data.push({ label: 'Treatment', value: formData.treatment });
    }
    return data;
  }, [formData]);

  // Patient data for context panel
  const patientData = useMemo(
    () => ({
      name: (() => {
        if (formData.firstName) {
          return `${formData.title || ''} ${formData.firstName} ${formData.lastName || ''}`.trim();
        }
        if (leadDetail?.first_name) {
          return safeJoinName(leadDetail.first_name, leadDetail.last_name);
        }
        return 'Unknown';
      })(),
      initials: (() => {
        if (formData.firstName) {
          return `${formData.firstName[0]}${formData.lastName?.[0] || ''}`.toUpperCase();
        }
        if (leadDetail?.first_name) {
          return `${leadDetail.first_name[0]}${leadDetail.last_name?.[0] || ''}`.toUpperCase();
        }
        return '?';
      })(),
      status: 'Viewing saved submission',
    }),
    [formData, leadDetail]
  );

  // Handlers
  const handleFormChange = useCallback((field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (validationErrors.length > 0) {
      setValidationErrors([]);
    }
  }, [validationErrors.length]);

  const handleNext = useCallback(() => {
    const currentCardData = cards[currentCardIndex];
    const hasUnfilledRequired = currentCardData?.elements?.some((element) => {
      if (!element.required) return false;
      const fieldName = element.field;
      const value = formData[fieldName];
      return value === undefined || value === null || value === '' || (Array.isArray(value) && value.length === 0);
    });

    if (hasUnfilledRequired) {
      setSkippedCards((prev) => new Set([...prev, currentCardIndex]));
      setCompletedCards((prev) => {
        const newSet = new Set(prev);
        newSet.delete(currentCardIndex);
        return newSet;
      });
    } else {
      setCompletedCards((prev) => new Set([...prev, currentCardIndex]));
      setSkippedCards((prev) => {
        const newSet = new Set(prev);
        newSet.delete(currentCardIndex);
        return newSet;
      });
    }

    if (currentCardIndex < cards.length - 1) {
      setCurrentCardIndex((prev) => prev + 1);
    }
  }, [currentCardIndex, cards, formData]);

  const handleBack = useCallback(() => {
    if (currentCardIndex > 0) {
      setCurrentCardIndex((prev) => prev - 1);
    }
  }, [currentCardIndex]);

  const handleSkip = useCallback(() => {
    setSkippedCards((prev) => new Set([...prev, currentCardIndex]));
    if (currentCardIndex < cards.length - 1) {
      setCurrentCardIndex((prev) => prev + 1);
    }
  }, [currentCardIndex, cards.length]);

  const handleSectionClick = useCallback(
    (sectionId) => {
      const firstCardOfSection = cards.findIndex((c) => c.section === sectionId);
      if (firstCardOfSection !== -1) {
        setCurrentCardIndex(firstCardOfSection);
      }
    },
    [cards]
  );

  const handleQuestionClick = useCallback(
    (questionIndex) => {
      const cardIndex = cards.indexOf(currentSectionQuestions[questionIndex]);
      if (cardIndex !== -1) {
        setCurrentCardIndex(cardIndex);
      }
    },
    [cards, currentSectionQuestions]
  );

  const parseMergeTags = useCallback(
    (text) => {
      if (!text) return text;
      return text.replace(/\$[a-zA-Z0-9_]+/g, (match) => {
        const key = match.slice(1);
        return Object.prototype.hasOwnProperty.call(mergeTagData, key) ? mergeTagData[key] : match;
      });
    },
    [mergeTagData]
  );

  const handleNoteChange = useCallback(
    (note) => {
      setCardNotes((prev) => ({ ...prev, [currentCardIndex]: note }));
    },
    [currentCardIndex]
  );

  // Submit handler - updates the existing submission
  const handleSubmit = useCallback(() => {
    setIsSaving(true);
    setSaveStatus('saving');

    // Build submission fields from form data
    const submissionFields = [];

    // Map form data to submission fields
    Object.entries(formData).forEach(([key, value]) => {
      // Skip non-field keys (including special attributes that need custom handling)
      if (['lead_id', 'firstName', 'lastName', 'title', 'phone', 'email', 'treatment', 'treatmentValue', 'treatment_id', 'phone_number', 'date_of_birth', 'bookedWithIds', 'bookedWith', 'assignToState'].includes(key)) {
        return;
      }

      // Extract field ID from key like 'field_123'
      const fieldIdMatch = key.match(/^field_(\d+)$/);
      if (fieldIdMatch) {
        const fieldId = parseInt(fieldIdMatch[1], 10);
        // Handle different value types
        let fieldValue;
        if (Array.isArray(value)) {
          // Check if it's an array of objects (like users/assignees)
          if (value.length > 0 && typeof value[0] === 'object' && value[0] !== null) {
            // Extract IDs from objects (for bookedWith/assignees)
            fieldValue = value.map((item) => item.id).join(' | ');
          } else {
            // Regular array of primitives
            fieldValue = value.join(' | ');
          }
        } else {
          fieldValue = String(value || '');
        }
        const fieldEntry = { field: fieldId, value: fieldValue };

        // Add submission entry ID if we have it
        const submissionEntryId = fieldSubmissionMapRef.current[String(fieldId)];
        if (submissionEntryId) {
          fieldEntry.id = submissionEntryId;
        }

        submissionFields.push(fieldEntry);
      }
    });

    // Handle special named attributes that need to be saved
    // These are stored under named keys (address, leadType, isRegister, preferredContactMethod)
    // Map them back to their original submission field entries using specialAttributeMapRef
    const formKeyToAttributeMap = {
      address: 'lead_address',
      leadType: 'lead_source_type',
      isRegister: 'lead_is_registered_patient',
      preferredContactMethod: 'lead_preferred_contact_method',
    };

    Object.entries(formKeyToAttributeMap).forEach(([formKey, attributeName]) => {
      if (formKey in formData && formData[formKey] !== undefined && formData[formKey] !== '') {
        const attrInfo = specialAttributeMapRef.current[attributeName];
        if (attrInfo) {
          // We have the original field and submission entry IDs, use them
          const fieldEntry = {
            field: attrInfo.fieldId,
            value: String(formData[formKey] || ''),
          };
          if (attrInfo.submissionFieldId) {
            fieldEntry.id = attrInfo.submissionFieldId;
          }
          submissionFields.push(fieldEntry);
        }
      }
    });

    const updateData = {
      practice: Number(practiceId),
      lead: formData.lead_id || leadDetail?.id,
      formId,
      fields: submissionFields,
    };

    dispatch(
      updateSubmission(practiceId, formId, submissionId, updateData, (result) => {
        setIsSaving(false);
        if (result?.success) {
          setSaveStatus('saved');
          enqueueSnackbar('Submission updated successfully', {
            variant: 'success',
            anchorOrigin: { vertical: 'top', horizontal: 'center' },
          });
          setTimeout(() => {
            onClose();
          }, 500);
        } else {
          setSaveStatus('error');
          enqueueSnackbar('Failed to update submission', {
            variant: 'error',
            anchorOrigin: { vertical: 'top', horizontal: 'center' },
          });
        }
      })
    );
  }, [formData, practiceId, formId, submissionId, leadDetail, onClose, enqueueSnackbar]);

  // Treatment handlers
  const handleTreatmentClick = useCallback((event) => {
    setTreatmentAnchorEl(event.currentTarget);
    setTreatmentPopoverOpen(true);
  }, []);

  const handleTreatmentClose = useCallback(() => {
    setTreatmentPopoverOpen(false);
    setTreatmentAnchorEl(null);
  }, []);

  const handleSaveTreatment = useCallback(
    (treatmentInfo) => {
      setFormData((prev) => ({
        ...prev,
        treatment: treatmentInfo.selectedTreatment || '',
        treatment_id: treatmentInfo.selectedTreatmentId || '',
        treatmentValue: treatmentInfo.treatmentPrice || '',
        teethAmount: treatmentInfo.teethAmount || null,
      }));
      handleTreatmentClose();
    },
    [handleTreatmentClose]
  );

  const handleTreatmentAmountChange = useCallback((value) => {
    setFormData((prev) => ({ ...prev, treatmentValue: value }));
  }, []);

  const handleTeethChange = useCallback((event) => {
    setFormData((prev) => ({ ...prev, teethAmount: event.target.value }));
  }, []);

  // Assignee handlers
  const handleAssigneeChange = useCallback((assignees) => {
    setAssignToState(assignees);
    setFormData((prev) => ({ ...prev, bookedWith: assignees }));
    setUserAutoSelected(false);
  }, []);

  const handleAssigneePopupOpen = useCallback((event) => {
    setAssigneeAnchorEl(event.currentTarget);
    setAssigneePopupOpen(true);
  }, []);

  const handleAssigneePopupClose = useCallback(() => {
    setAssigneePopupOpen(false);
    setAssigneeAnchorEl(null);
    dispatch(clearPracticeUserData());
  }, []);

  // Keyboard shortcuts
  useEffect(() => {
    if (!open) return undefined;

    const handleKeyDown = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleNext();
      }
      if (e.key === 'Escape') {
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [open, handleNext, onClose]);

  // Helper to get icon component
  const getIconComponent = (iconName) => {
    const iconMap = {
      PersonAdd: 'mdi:account-plus',
      Person: 'mdi:account',
      Check: 'mdi:check',
      Close: 'mdi:close',
      Schedule: 'mdi:clock-outline',
      ScheduleSend: 'mdi:clock-check-outline',
      CheckCircle: 'mdi:check-circle',
      PhoneMissed: 'mdi:phone-missed',
      PersonOff: 'mdi:account-off',
      LocationOn: 'mdi:map-marker',
      DirectionsCar: 'mdi:car',
    };
    const iconKey = iconMap[iconName];
    return iconKey ? <Iconify icon={iconKey} width={20} height={20} /> : null;
  };

  // Check if current card was skipped
  const wasSkipped = skippedCards.has(currentCardIndex);

  // Questions with notes for current section
  const questionsWithNotes = currentSectionQuestions
    .map((_, idx) => {
      const cardIdx = cards.indexOf(currentSectionQuestions[idx]);
      return cardNotes[cardIdx] ? idx : null;
    })
    .filter((idx) => idx !== null);

  // Completed/skipped questions for current section
  const completedQuestionsInSection = currentSectionQuestions
    .map((_, idx) => {
      const cardIdx = cards.indexOf(currentSectionQuestions[idx]);
      return completedCards.has(cardIdx) ? idx : null;
    })
    .filter((idx) => idx !== null);

  const skippedQuestionsInSection = currentSectionQuestions
    .map((_, idx) => {
      const cardIdx = cards.indexOf(currentSectionQuestions[idx]);
      return skippedCards.has(cardIdx) ? idx : null;
    })
    .filter((idx) => idx !== null);

  // Render card content
  const renderCardContent = (card) => {
    if (!card) return null;

    const elements = [];

    card.elements?.forEach((element, idx) => {
      switch (element.type) {
        case 'say': {
          const { text: sayText, hint: sayHint } = extractHint(element.text);
          elements.push(
            <SayBlock
              key={idx}
              text={sayText}
              tag={element.tag || 'Say'}
              variant={element.variant || 'primary'}
              mergeTagData={mergeTagData}
            />
          );
          if (sayHint) {
            elements.push(<HintAlert key={`${idx}-hint`} text={sayHint} />);
          }
          break;
        }

        case 'tip': {
          const { text: tipText, hint: tipHint } = extractHint(element.text);
          elements.push(<TipBlock key={idx} text={parseMergeTags(tipText)} icon={element.icon} />);
          if (tipHint) {
            elements.push(<HintAlert key={`${idx}-hint`} text={tipHint} />);
          }
          break;
        }

        case 'textField':
          elements.push(
            <FormRow key={idx}>
              <ScriptTextField
                label={parseMergeTags(element.label)}
                required={element.required}
                placeholder={element.placeholder}
                value={formData[element.field] || formData[`field_${element.id}`] || ''}
                onChange={(e) => handleFormChange(element.id ? `field_${element.id}` : element.field, e.target.value)}
                width={element.width}
                disabled={element.disabled}
              />
            </FormRow>
          );
          break;

        case 'select':
          elements.push(
            <FormRow key={idx}>
              <ScriptSelect
                label={parseMergeTags(element.label)}
                required={element.required}
                placeholder={element.placeholder}
                options={element.options}
                value={formData[element.field] || formData[`field_${element.id}`] || ''}
                onChange={(e) => handleFormChange(element.id ? `field_${element.id}` : element.field, e.target.value)}
                width={element.width}
              />
            </FormRow>
          );
          break;

        case 'textarea':
          elements.push(
            <FormRow key={idx}>
              <ScriptTextarea
                label={parseMergeTags(element.label)}
                required={element.required}
                placeholder={element.placeholder}
                rows={element.rows}
                value={formData[element.field] || formData[`field_${element.id}`] || ''}
                onChange={(e) => handleFormChange(element.id ? `field_${element.id}` : element.field, e.target.value)}
              />
            </FormRow>
          );
          break;

        case 'date':
          elements.push(
            <FormRow key={idx}>
              <ScriptDateField
                label={parseMergeTags(element.label)}
                required={element.required}
                value={formData[element.field] || formData[`field_${element.id}`] || ''}
                onChange={(e) => handleFormChange(element.id ? `field_${element.id}` : element.field, e.target.value)}
                width={element.width}
              />
            </FormRow>
          );
          break;

        case 'choiceGrid': {
          const optionValues =
            element.options?.map((opt) => (typeof opt === 'string' ? opt : opt.value)?.toLowerCase()) || [];
          const isInterestLevel =
            optionValues.includes('hot') && optionValues.includes('warm') && optionValues.includes('cold');

          if (isInterestLevel && element.choiceType === 'radio') {
            const interestOptions = element.options.map((opt) => {
              const value = typeof opt === 'string' ? opt : opt.value;
              const label = typeof opt === 'string' ? opt : opt.label;
              const valueLower = value?.toLowerCase();
              const defaults = {
                hot: { emoji: '🔥', description: 'Highly interested', variant: 'hot' },
                warm: { emoji: '☀️', description: 'Some interest', variant: 'warm' },
                cold: { emoji: '❄️', description: 'Not ready', variant: 'cold' },
              };
              return {
                value,
                label: label?.toUpperCase() || value?.toUpperCase(),
                emoji: opt.emoji || defaults[valueLower]?.emoji || '',
                description: opt.description || defaults[valueLower]?.description || '',
                variant: defaults[valueLower]?.variant || 'default',
              };
            });

            const fieldKey = element.id ? `field_${element.id}` : element.field;
            elements.push(
              <Box key={idx}>
                <InterestCards
                  options={interestOptions}
                  selectedValue={formData[fieldKey]?.[0] || formData[fieldKey]}
                  onChange={(value) => handleFormChange(fieldKey, [value])}
                />
              </Box>
            );
          } else {
            const fieldKey = element.id ? `field_${element.id}` : element.field;
            elements.push(
              <ChoiceGrid
                key={idx}
                options={element.options}
                selectedValues={formData[fieldKey] || []}
                onChange={(values) => handleFormChange(fieldKey, values)}
                type={element.choiceType || 'checkbox'}
                single={element.single}
                name={element.field}
              />
            );
          }
          break;
        }

        case 'responseButtons':
          elements.push(
            <ResponseButtons
              key={idx}
              options={element.options?.map((opt) => ({
                ...opt,
                icon: getIconComponent(opt.iconName),
              }))}
              selectedValue={formData[element.field] || formData[`field_${element.id}`]}
              onChange={(value) => handleFormChange(element.id ? `field_${element.id}` : element.field, value)}
              vertical={element.vertical}
            />
          );
          break;

        case 'scaleRating':
          elements.push(
            <ScaleRating
              key={idx}
              min={element.min || 1}
              max={element.max || 10}
              value={formData[element.field] || formData[`field_${element.id}`]}
              onChange={(value) => handleFormChange(element.id ? `field_${element.id}` : element.field, value)}
            />
          );
          break;

        case 'sectionDivider':
          elements.push(
            <SectionDivider key={idx} title={parseMergeTags(element.title)} subtitle={parseMergeTags(element.subtitle)} />
          );
          break;

        case 'interestCards':
          elements.push(
            <InterestCards
              key={idx}
              options={element.options}
              selectedValue={formData[element.field] || formData[`field_${element.id}`]}
              onChange={(value) => handleFormChange(element.id ? `field_${element.id}` : element.field, value)}
            />
          );
          break;

        case 'leadSelector':
        case 'lead_search_select':
          elements.push(
            <FormRow key={idx}>
              <LeadSearchField
                label={parseMergeTags(element.label)}
                value={formData.leadName || ''}
                onChange={(value) => handleFormChange('leadName', value)}
                onLeadSelect={() => {}}
                required={element.required}
                disabled
                placeholder="Lead is fixed for saved submissions"
              />
            </FormRow>
          );
          if (formData.lead_id || leadDetail?.id) {
            elements.push(
              <FormRow key={`${idx}-lead-details`}>
                <ScriptSelect
                  label="Title"
                  placeholder="Select title"
                  options={salutationOptions?.length ? salutationOptions.map((opt) => opt.value) : CANONICAL_SALUTATION_VALUES}
                  value={formData.title || ''}
                  onChange={(e) => handleFormChange('title', e.target.value)}
                  width="25%"
                  disabled
                />
                <ScriptTextField
                  label="First Name"
                  required
                  placeholder="First name"
                  value={formData.firstName || ''}
                  onChange={(e) => handleFormChange('firstName', e.target.value)}
                  width="37.5%"
                  disabled
                />
                <ScriptTextField
                  label="Last Name"
                  placeholder="Last name"
                  value={formData.lastName || ''}
                  onChange={(e) => handleFormChange('lastName', e.target.value)}
                  width="37.5%"
                  disabled
                />
              </FormRow>
            );
          }
          break;

        case 'treatmentSelector':
        case 'practice_treatment_search_select':
          elements.push(
            <FormRow key={idx}>
              <Box sx={{ width: '100%' }} ref={treatmentButtonRef}>
                {treatmentPopoverOpen && (
                  <TreatmentEditPopover
                    treatment={formData.treatment || ''}
                    leadTreatmentSpecification={{ teeth_count: formData.teethAmount }}
                    open={treatmentPopoverOpen}
                    anchorEl={treatmentAnchorEl}
                    handleClickAwayLister={handleTreatmentClose}
                    treatmentPrice={formData.treatmentValue}
                    handleSaveTreatment={handleSaveTreatment}
                    isFlow
                    isScript
                  />
                )}
                <TreatmentFlowComponent
                  treatmentData={formData.treatment}
                  teethAmount={formData.teethAmount}
                  handleTreatment={handleTreatmentClick}
                  amount={formData.treatmentValue}
                  amountError={false}
                  amountHandleChange={handleTreatmentAmountChange}
                  handleChangeTeeth={handleTeethChange}
                  teethAmountError={false}
                />
              </Box>
            </FormRow>
          );
          break;

        case 'assigneeSelector':
        case 'practice_user_search_select': {
          const selectedUsers = assignToState || [];
          const hasUsers = selectedUsers.length > 0;
          const isLoadingUsers = !practiceUser?.results && practiceId;

          const getUserInfo = (user) => {
            const name = user?.user ? `${user.user.first_name || ''} ${user.user.last_name || ''}`.trim() : '';
            const initials = user?.user
              ? `${user.user.first_name?.[0] || ''}${user.user.last_name?.[0] || ''}`.toUpperCase()
              : '';
            return { name, initials, avatar: user?.user?.avatar || '' };
          };

          const renderAssigneeContent = () => {
            if (isLoadingUsers) {
              return (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Skeleton variant="circular" width={36} height={36} />
                  <Skeleton variant="text" width={120} height={24} />
                </Box>
              );
            }

            if (hasUsers) {
              const displayName =
                selectedUsers.length === 1
                  ? getUserInfo(selectedUsers[0]).name || 'Unknown User'
                  : `${selectedUsers.length} users selected`;

              return (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    p: 1,
                    borderRadius: '10px',
                    bgcolor: 'rgba(145, 158, 171, 0.08)',
                    border: '1px solid rgba(145, 158, 171, 0.16)',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {selectedUsers.slice(0, 2).map((user, index) => {
                      const { initials, avatar } = getUserInfo(user);
                      return (
                        <Avatar
                          key={user.id || index}
                          src={avatar}
                          sx={{
                            width: 36,
                            height: 36,
                            bgcolor: index === 0 ? '#00A76F' : '#637381',
                            fontSize: '0.875rem',
                            fontWeight: 600,
                            marginLeft: index > 0 ? '-10px' : 0,
                            border: '2px solid #fff',
                            zIndex: 2 - index,
                          }}
                        >
                          {initials}
                        </Avatar>
                      );
                    })}
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: '#212B36' }}>
                      {displayName}
                    </Typography>
                  </Box>
                  <IconButton
                    size="small"
                    onClick={handleAssigneePopupOpen}
                    sx={{ color: '#637381', '&:hover': { bgcolor: 'rgba(0, 167, 111, 0.08)', color: '#00A76F' } }}
                  >
                    <Iconify icon="mdi:pencil" width={18} height={18} />
                  </IconButton>
                </Box>
              );
            }

            return (
              <Box
                onClick={handleAssigneePopupOpen}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  p: 1,
                  borderRadius: '10px',
                  border: '1px dashed rgba(145, 158, 171, 0.32)',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  '&:hover': { borderColor: '#00A76F', bgcolor: 'rgba(0, 167, 111, 0.04)' },
                }}
              >
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: 'rgba(145, 158, 171, 0.16)',
                  }}
                >
                  <Iconify icon="mdi:plus" width={20} height={20} sx={{ color: '#637381' }} />
                </Box>
                <Typography sx={{ fontSize: '0.875rem', color: '#637381' }}>Select user</Typography>
              </Box>
            );
          };

          elements.push(
            <FormRow key={idx}>
              <Box sx={{ width: '100%' }}>
                {renderAssigneeContent()}
                {assigneePopupOpen && (
                  <ClickAwayListener onClickAway={handleAssigneePopupClose}>
                    <Popper
                      open={assigneePopupOpen}
                      anchorEl={assigneeAnchorEl}
                      transition
                      placement="bottom-start"
                      sx={{ zIndex: 1300 }}
                    >
                      {({ TransitionProps }) => (
                        <Grow {...TransitionProps}>
                          <Paper sx={{ borderRadius: 2, boxShadow: '0 8px 24px rgba(0,0,0,0.12)' }}>
                            <AssignTaskPopup
                              open={assigneePopupOpen}
                              onClose={handleAssigneePopupClose}
                              type="AddReminder"
                              setAssignTo={handleAssigneeChange}
                              assignees={assignToState?.map((ele) => ele.id)}
                            />
                          </Paper>
                        </Grow>
                      )}
                    </Popper>
                  </ClickAwayListener>
                )}
              </Box>
            </FormRow>
          );
          break;
        }

        // Special attribute field types for call scripts - display submitted values using live components
        case 'capture_contact_details': {
          // Pre-populate with leadDetail values as fallbacks
          const leadContactDefaults = {
            phone_number: leadDetail?.lead_contact?.phone || '',
            email: leadDetail?.lead_contact?.email || '',
            date_of_birth: leadDetail?.date_of_birth || leadDetail?.dob || '',
            address_line_1: leadDetail?.lead_contact?.address_line_1 || leadDetail?.lead_contact?.address?.line1 || '',
            address_line_2: leadDetail?.lead_contact?.address_line_2 || leadDetail?.lead_contact?.address?.line2 || '',
            city: leadDetail?.lead_contact?.city || leadDetail?.lead_contact?.address?.city || '',
            county: leadDetail?.lead_contact?.county || leadDetail?.lead_contact?.address?.county || '',
            postcode: leadDetail?.lead_contact?.postcode || leadDetail?.lead_contact?.address?.postcode || '',
          };

          // Parse JSON value for contact details and merge with defaults
          const contactData = { ...leadContactDefaults };
          try {
            const submittedData = typeof element.value === 'string' ? JSON.parse(element.value) : (element.value || {});
            // Only override defaults with non-empty submitted values
            Object.entries(submittedData).forEach(([key, val]) => {
              if (val !== undefined && val !== null && val !== '') {
                contactData[key] = val;
              }
            });
          } catch (e) {
            // Keep defaults on parse error
          }

          // Define referral options for the grid
          const referralOptions = [
            { value: 'GOOGLE', label: 'Google', icon: 'devicon:google' },
            { value: 'FACEBOOK', label: 'Facebook', icon: 'logos:facebook' },
            { value: 'INSTAGRAM', label: 'Instagram', icon: 'skill-icons:instagram' },
            { value: 'FRIEND', label: 'Friend', icon: 'solar:users-group-rounded-bold' },
            { value: 'EMAIL', label: 'Email', icon: 'solar:letter-bold' },
            { value: 'PHONE', label: 'Phone', icon: 'solar:phone-bold' },
            { value: 'WALK_IN', label: 'Walk-in', icon: 'solar:walking-bold' },
            { value: 'EVENTS', label: 'Events', icon: 'solar:calendar-bold' },
            { value: 'REFERRAL', label: 'Referral', icon: 'solar:share-bold' },
            { value: 'OTHER', label: 'Other', icon: 'solar:question-circle-bold' },
          ];

          elements.push(
            <CaptureContactDetailsLive
              key={idx}
              formData={contactData}
              onChange={(field, value) => {
                // Update the contact details in formData
                const updatedContactData = { ...contactData, [field]: value };
                handleFormChange(`field_${element.id}`, JSON.stringify(updatedContactData));
              }}
              referralOptions={referralOptions}
            />
          );
          break;
        }

        case 'confirm_speaker': {
          elements.push(
            <ConfirmSpeakerLive
              key={idx}
              formData={{ confirmSpeaker: element.value }}
              onChange={(field, value) => {
                handleFormChange(`field_${element.id}`, value);
              }}
            />
          );
          break;
        }

        case 'answer_the_call': {
          elements.push(
            <Box
              key={idx}
              sx={{
                width: '100%',
                border: '1px solid rgba(145, 158, 171, 0.32)',
                borderRadius: '12px',
                p: 2,
                backgroundColor: '#fff',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: 'rgba(0, 184, 217, 0.08)',
                  }}
                >
                  <Iconify icon="solar:phone-calling-bold" sx={{ width: 18, height: 18, color: '#00B8D9' }} />
                </Box>
                <Box>
                  <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: '#212B36' }}>
                    Call Answered
                  </Typography>
                  {element.value && (
                    <Typography sx={{ fontSize: '0.75rem', color: '#637381' }}>
                      Response: {element.value}
                    </Typography>
                  )}
                </Box>
              </Box>
            </Box>
          );
          break;
        }

        case 'identify_caller': {
          const CALLER_OPTIONS = [
            { value: 'new_patient', title: 'New Patient', subtitle: 'First time caller', icon: 'solar:user-plus-bold', color: '#00A76F', bgColor: '#00A76F14' },
            { value: 'existing_patient', title: 'Existing Patient', subtitle: 'Already in system', icon: 'solar:user-check-bold', color: '#006C9C', bgColor: '#006C9C14' },
          ];
          const selectedValue = element.value;

          elements.push(
            <Box key={idx} sx={{ width: '100%' }}>
              <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                {CALLER_OPTIONS.map((option) => {
                  const isSelected = selectedValue === option.value;
                  return (
                    <Box
                      key={option.value}
                      sx={{
                        flex: '1 1 auto',
                        minWidth: 140,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        p: 1.5,
                        borderRadius: '12px',
                        border: isSelected ? '2px solid #00A76F' : '1.5px solid rgba(145, 158, 171, 0.32)',
                        backgroundColor: isSelected ? 'rgba(0, 167, 111, 0.08)' : '#fff',
                        opacity: isSelected ? 1 : 0.5,
                      }}
                    >
                      <Box
                        sx={{
                          width: 36,
                          height: 36,
                          borderRadius: '10px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: option.bgColor,
                          flexShrink: 0,
                        }}
                      >
                        <Iconify icon={option.icon} sx={{ width: 20, height: 20, color: option.color }} />
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Typography sx={{ fontSize: '0.8rem', fontWeight: 500, color: '#212B36', lineHeight: 1.3 }}>
                          {option.title}
                        </Typography>
                        <Typography sx={{ fontSize: '0.7rem', color: '#919EAB', lineHeight: 1.3 }}>
                          {option.subtitle}
                        </Typography>
                      </Box>
                      {isSelected && (
                        <Iconify icon="mdi:check-circle" sx={{ width: 20, height: 20, color: '#00A76F' }} />
                      )}
                    </Box>
                  );
                })}
              </Box>
            </Box>
          );
          break;
        }

        case 'reason_for_calling': {
          // Parse JSON value if it's a complex object
          let reasonData = element.value;
          try {
            reasonData = typeof element.value === 'string' && element.value.startsWith('{')
              ? JSON.parse(element.value)
              : element.value;
          } catch (e) {
            reasonData = element.value;
          }

          elements.push(
            <Box
              key={idx}
              sx={{
                width: '100%',
                border: '1px solid rgba(145, 158, 171, 0.32)',
                borderRadius: '12px',
                p: 2,
                backgroundColor: '#fff',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: 'rgba(118, 53, 220, 0.08)',
                  }}
                >
                  <Iconify icon="solar:chat-round-call-bold" sx={{ width: 18, height: 18, color: '#7635DC' }} />
                </Box>
                <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: '#212B36' }}>
                  Reason for Calling
                </Typography>
              </Box>
              <Typography sx={{ fontSize: '0.875rem', color: '#212B36' }}>
                {typeof reasonData === 'object' ? JSON.stringify(reasonData, null, 2) : reasonData}
              </Typography>
            </Box>
          );
          break;
        }

        case 'close_call': {
          // Parse JSON value for close call data
          let closeData = {};
          try {
            closeData = typeof element.value === 'string' ? JSON.parse(element.value) : (element.value || {});
          } catch (e) {
            closeData = {};
          }

          const INTEREST_OPTIONS = [
            { value: 'hot', label: 'HOT', emoji: '🔥', description: 'Highly interested', color: '#22C55E', bgColor: '#22C55E14' },
            { value: 'warm', label: 'WARM', emoji: '☀️', description: 'Some interest', color: '#FFAB00', bgColor: '#FFAB0014' },
            { value: 'cold', label: 'COLD', emoji: '❄️', description: 'Not ready', color: '#00B8D9', bgColor: '#00B8D914' },
          ];

          const OUTCOME_OPTIONS = [
            { value: 'submit', label: 'Submit', icon: 'formkit:submit', color: '#637381' },
            { value: 'not-booked', label: 'Appointment Not Booked', icon: 'ant-design:calendar-filled', color: '#FF5630' },
            { value: 'booked', label: 'Book An Appointment', icon: 'akar-icons:circle-check-fill', color: '#22C55E' },
          ];

          const selectedInterest = closeData.interestLevel;
          const selectedOutcome = closeData.callOutcome;

          elements.push(
            <Box key={idx} sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 3 }}>
              {/* Interest Level Section */}
              <Box>
                <Typography sx={{ fontSize: '0.9375rem', fontWeight: 600, color: '#212B36', mb: 0.5 }}>
                  Interest Level
                </Typography>
                <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                  {INTEREST_OPTIONS.map((option) => {
                    const isSelected = selectedInterest === option.value;
                    return (
                      <Box
                        key={option.value}
                        sx={{
                          flex: '1 1 auto',
                          minWidth: 100,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1.5,
                          p: 1.5,
                          borderRadius: '12px',
                          border: isSelected ? `2px solid ${option.color}` : '1.5px solid rgba(145, 158, 171, 0.32)',
                          backgroundColor: isSelected ? option.bgColor : '#fff',
                          opacity: isSelected ? 1 : 0.5,
                        }}
                      >
                        <Box
                          sx={{
                            width: 36,
                            height: 36,
                            borderRadius: '8px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: option.bgColor,
                            fontSize: '20px',
                          }}
                        >
                          {option.emoji}
                        </Box>
                        <Box>
                          <Typography sx={{ fontSize: '0.875rem', fontWeight: 700, color: '#212B36' }}>
                            {option.label}
                          </Typography>
                          <Typography sx={{ fontSize: '0.7rem', color: '#637381' }}>
                            {option.description}
                          </Typography>
                        </Box>
                        {isSelected && (
                          <Iconify icon="mdi:check-circle" sx={{ width: 20, height: 20, color: option.color, ml: 'auto' }} />
                        )}
                      </Box>
                    );
                  })}
                </Box>
              </Box>

              {/* Outcome Section */}
              <Box>
                <Typography sx={{ fontSize: '0.9375rem', fontWeight: 600, color: '#212B36', mb: 0.5 }}>
                  Call Outcome
                </Typography>
                <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                  {OUTCOME_OPTIONS.map((option) => {
                    const isSelected = selectedOutcome === option.value;
                    return (
                      <Box
                        key={option.value}
                        sx={{
                          flex: '1 1 auto',
                          minWidth: 120,
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          gap: 1,
                          p: 2,
                          borderRadius: '12px',
                          border: isSelected ? '2px solid #00A76F' : '1.5px solid rgba(145, 158, 171, 0.32)',
                          backgroundColor: isSelected ? 'rgba(0, 167, 111, 0.08)' : '#fff',
                          opacity: isSelected ? 1 : 0.5,
                        }}
                      >
                        <Iconify icon={option.icon} sx={{ width: 28, height: 28, color: isSelected ? '#00A76F' : option.color }} />
                        <Typography sx={{ fontSize: '0.75rem', fontWeight: 500, color: '#212B36', textAlign: 'center' }}>
                          {option.label}
                        </Typography>
                        {isSelected && (
                          <Iconify icon="mdi:check-circle" sx={{ width: 18, height: 18, color: '#00A76F' }} />
                        )}
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            </Box>
          );
          break;
        }

        default:
          break;
      }
    });

    // Add note toggle
    elements.push(
      <NoteToggle key="note" value={cardNotes[currentCardIndex] || ''} onChange={handleNoteChange} />
    );

    return elements;
  };

  // Loading state
  if (isLoading) {
    return (
      <Dialog open={open} onClose={onClose} fullScreen>
        <DialogContent
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: '#F4F6F8',
          }}
        >
          <Box sx={{ textAlign: 'center' }}>
            <CircularProgress size={48} sx={{ color: '#00A76F', mb: 2 }} />
            <Typography sx={{ color: '#637381' }}>Loading saved submission...</Typography>
          </Box>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onClose={onClose} fullScreen>
      <DialogContent sx={{ p: 0, overflow: 'hidden' }}>
        <Box sx={appContainerSx}>
          <CallScriptHeader
            title={scriptData?.title || 'Submitted Callscript'}
            subtitle={scriptData?.subtitle || 'Viewing saved submission'}
            type="outbound"
            currentStep={currentStep}
            totalSteps={totalSteps}
            saveStatus={saveStatus}
            onClose={onClose}
            onToggleContext={() => setContextPanelOpen(!contextPanelOpen)}
            showContextToggle
          />

          <Box sx={mainContainerSx}>
            <Box sx={scriptPanelSx}>
              {/* Informational banner when original script is deleted */}
              {scriptLoadError && (
                <Box
                  sx={{
                    mb: 2,
                    p: 2,
                    borderRadius: '12px',
                    bgcolor: 'rgba(255, 171, 0, 0.08)',
                    border: '1px solid rgba(255, 171, 0, 0.24)',
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 1.5,
                  }}
                >
                  <Iconify
                    icon="mdi:information-outline"
                    width={20}
                    height={20}
                    sx={{ color: '#FFAB00', flexShrink: 0, mt: 0.25 }}
                  />
                  <Box>
                    <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: '#B76E00', mb: 0.25 }}>
                      Original Script Not Found
                    </Typography>
                    <Typography sx={{ fontSize: '0.8125rem', color: '#B76E00', lineHeight: 1.5 }}>
                      The original call script used for this submission has been deleted or modified.
                      All submitted responses are shown in the "Other Responses" section.
                    </Typography>
                  </Box>
                </Box>
              )}

              <SectionTabs
                sections={sections}
                currentSectionId={currentSectionId}
                completedSections={completedSections}
                sectionsWithSkipped={sectionsWithSkipped}
                onSectionClick={handleSectionClick}
              />

              <QuestionProgress
                questions={currentSectionQuestions}
                currentQuestionIndex={currentQuestionInSection}
                completedQuestions={completedQuestionsInSection}
                skippedQuestions={skippedQuestionsInSection}
                questionsWithNotes={questionsWithNotes}
                onQuestionClick={handleQuestionClick}
              />

              <Box sx={cardAreaSx}>
                {currentCard && (
                  <ScriptCard
                    badge={currentCard.badge}
                    badgeClass={currentCard.badgeClass}
                    title={parseMergeTags(currentCard.title)}
                    subtitle={parseMergeTags(currentCard.subtitle)}
                    wasSkipped={wasSkipped}
                    onUnskip={() => {
                      setSkippedCards((prev) => {
                        const newSet = new Set(prev);
                        newSet.delete(currentCardIndex);
                        return newSet;
                      });
                    }}
                  >
                    {renderCardContent(currentCard)}
                  </ScriptCard>
                )}
              </Box>

              <StickyFooter
                onBack={handleBack}
                onSkip={handleSkip}
                onSave={handleSubmit}
                onNext={currentCardIndex < cards.length - 1 ? handleNext : handleSubmit}
                isBackDisabled={currentCardIndex === 0}
                isComplete={currentCardIndex >= cards.length - 1}
                nextLabel={(() => {
                  if (currentCardIndex < cards.length - 1) return 'Continue';
                  return isSaving ? 'Saving...' : 'Submit';
                })()}
                showSkip={currentCardIndex < cards.length - 1}
                showKeyboardHint={currentCardIndex < cards.length - 1}
              />
            </Box>

            <ContextPanel
              type="outbound"
              patient={patientData}
              tags={[]}
              progress={progressData}
              collectedData={collectedData}
              notes={callNotes}
              onSectionClick={handleSectionClick}
              onQuestionClick={(cardIndex) => setCurrentCardIndex(cardIndex)}
              cards={cards}
              currentCardIndex={currentCardIndex}
              completedCards={completedCards}
              skippedCards={skippedCards}
              isOpen={contextPanelOpen}
              mergeTagData={mergeTagData}
            />
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

SavedCallScriptViewer.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  formId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  submissionId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  leadDetail: PropTypes.object,
};

SavedCallScriptViewer.defaultProps = {
  leadDetail: null,
};

export default SavedCallScriptViewer;
